// src/Frontend/LoginDR12/OAuthAuthorize.jsx
//
// Login + consent screen for the "Add connector" OAuth flow (claude.ai and
// similar MCP clients). node-datafiniti-api's OAuth provider
// (src/oauth/provider.js there) redirects here with client_id/redirect_uri/
// code_challenge/state/scope in the query string; on submit we POST those
// plus the entered credentials to /oauth/consent, which verifies the
// password and hands back a { redirect_url } to send the browser to,
// completing the authorization-code handshake back on the client's side.
//
// Deliberately always shows the credential form, even for a visitor with an
// existing portal session (unlike PublicRoute's normal restricted={false}
// redirect-away behavior) — re-confirming identity for a third-party
// authorization is standard, and it keeps /oauth/consent's contract simple
// (always email+password, no separate existing-session code path).
import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { authorizeOAuthConsent } from '../../Api'
import styles from './styles.module.css'
import DatafinitiLogo from '../HomeDR12/images/datafiniti-logo-white.svg'
import { useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import { CircularProgress } from '@material-ui/core'
import BaseAlert from '../Snackbar/NewImplementations/BaseAlert/BaseAlert'
import ColorAlert from '../Snackbar/NewImplementations/ColorAlert/ColorAlert'
import useAlerts from '../Snackbar/NewImplementations/HooksAlerts/useAlerts'
import useServerAlert from '../Snackbar/NewImplementations/HooksAlerts/useServerAlert'

export default function OAuthAuthorize () {
  const query = new URLSearchParams(useLocation().search)
  const client_id = query.get('client_id')
  const redirect_uri = query.get('redirect_uri')
  const code_challenge = query.get('code_challenge')
  const state = query.get('state')
  const scope = query.get('scope')

  const [userData, setUserData] = useState({ email: '', password: '' })
  const { register, handleSubmit } = useForm()

  const { alerts, closeAlert, showValidationErrors } = useAlerts()
  const { alertData, showError, onClose: closeServerAlert } = useServerAlert()

  const handleInputChange = ({ target }) => {
    setUserData(d => ({ ...d, [target.name]: target.value }))
  }

  const [mutateConsent, { isLoading }] = useMutation(
    () => authorizeOAuthConsent({ ...userData, client_id, redirect_uri, code_challenge, state, scope }),
    {
      onSuccess (data) {
        if (data.status >= 400) {
          showError(
            'Invalid login',
            'Please check your credentials and try again.'
          )
        } else {
          // Full browser navigation, not a client-side route change — this
          // needs to leave the portal's origin and land back on the OAuth
          // client (e.g. claude.ai).
          window.location.href = data.data.redirect_url
        }
      },
      onError () {
        showError(
          'Connection Error',
          'Failed to connect to the server. Please try again.'
        )
      }
    }
  )

  const onSubmit = () => {
    if (!isLoading) mutateConsent()
  }

  if (!client_id || !redirect_uri || !code_challenge) {
    return (
      <section className={styles.mainContainer}>
        <div className={styles.background} />
        <div className={styles.contentContainer}>
          <img src={DatafinitiLogo} alt='DatafinitiLogo' className={styles.logo} />
          <p style={{ color: 'white' }}>This authorization link is missing required parameters.</p>
        </div>
      </section>
    )
  }

  return (
    <section className={styles.mainContainer}>
      <div className={styles.background} />
      <div className={styles.contentContainer}>
        <img src={DatafinitiLogo} alt='DatafinitiLogo' className={styles.logo} />
        <div className={styles.formContainer}>
          <p style={{ color: 'white', marginBottom: '1em' }}>
            An application wants to connect to your Datafiniti account. Log in
            to allow access.
          </p>
          <form
            id='Datafiniti-Portal-OAuth-Authorize'
            name='Datafiniti-Portal-OAuth-Authorize'
            onSubmit={handleSubmit(onSubmit, showValidationErrors)}
            className={styles.formLogin}
          >
            <div className={styles.inputContainer}>
              <input
                name='email'
                placeholder='Email'
                className={styles.inputField}
                autoFocus
                autoComplete='off'
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,15}$/i,
                    message: 'Invalid email format'
                  },
                  onChange: handleInputChange
                })}
                value={userData.email}
              />
              <input
                name='password'
                type='password'
                placeholder='Password'
                className={styles.inputField}
                autoComplete='off'
                {...register('password', {
                  required: 'Password is required',
                  onChange: handleInputChange
                })}
                value={userData.password}
              />
            </div>

            <div className={styles.buttonContainer}>
              <button
                type='submit'
                className={styles.loginButton}
                disabled={isLoading}
                data-retry='true'
              >
                {isLoading ? (
                  <CircularProgress size={24} className={styles.circularProgress} />
                ) : (
                  'Allow access'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {alerts.map((a, i) => (
        <ColorAlert
          key={a.id}
          open={a.open}
          type={a.type}
          label={a.message}
          onClose={() => closeAlert(a.id)}
          style={{ bottom: 20 + i * 80 }}
        />
      ))}

      <BaseAlert
        open={!!alertData}
        title={alertData?.title}
        body={alertData?.body}
        actions={alertData?.actions}
        onClose={closeServerAlert}
      />
    </section>
  )
}
